import { useCallback, useEffect, useState } from "react";
import { AlertCircle, ArrowLeft, CalendarDays, LockKeyhole } from "lucide-react";
import { Link } from "react-router-dom";
import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { usePerformanceLensAccess } from "../../lib/performance-lens/use-access.ts";
import { getCycleConfiguration } from "../../lib/performance-lens/review-api.ts";
import type { CycleConfig } from "../../lib/performance-lens/types.ts";
import AdminConfigPanel from "./admin-config-panel.tsx";
import ScreenSkeleton from "../../components/ui/screen-skeleton/screen-skeleton.tsx";
import styles from "./performance-configuration-page.module.css";

function initialCycle(cycles: CycleConfig[]): number | null {
  return cycles.find(cycle => cycle.status === "OPEN")?.cycleNumber
    ?? [...cycles].filter(cycle => cycle.status === "PLANNED")
      .sort((first, second) => first.cycleNumber - second.cycleNumber)[0]?.cycleNumber
    ?? cycles.find(cycle => cycle.status === "CLOSED")?.cycleNumber
    ?? cycles[0]?.cycleNumber ?? null;
}

export default function PerformanceConfigurationPage() {
  const { access, loading: accessLoading, error: accessError, refresh } = usePerformanceLensAccess();
  const [cycles, setCycles] = useState<CycleConfig[]>([]);
  const [cycleNumber, setCycleNumber] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true); setError("");
    try {
      const result = await getCycleConfiguration(signal);
      if (signal?.aborted) return;
      setCycles(result);
      setCycleNumber(previous => previous !== null && result.some(cycle => cycle.cycleNumber === previous)
        ? previous : initialCycle(result));
    } catch (cause) {
      if (!signal?.aborted) setError(cause instanceof Error ? cause.message : "Could not load cycle configuration.");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!access?.admin) return;
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [access?.admin, load]);

  return <PageShell className={styles.shell}><main className={styles.page}>
    <div className={styles.pageActions}>
      {!accessLoading && access?.admin && <div className={styles.sectionNav}><span className={styles.activeSection}><CalendarDays size={17} /> Cycles &amp; Questions</span></div>}
      <Link to="/dashboard/performanceDashboard?view=admin" className={styles.back}><ArrowLeft size={16} /> Performance Dashboard</Link>
    </div>
    {accessLoading && <ScreenSkeleton label="Checking administrator access" variant="configuration" />}
    {accessError && <div className={styles.notice} role="alert"><AlertCircle size={18} />Could not verify access.<button type="button" onClick={() => void refresh()}>Retry</button></div>}
    {!accessLoading && !accessError && !access?.admin && <div className={styles.notice}><LockKeyhole size={20} /><div><strong>Access Restricted</strong><p>Only configured Performance Lens administrators can manage questions.</p></div></div>}
    {!accessLoading && access?.admin && <>
      {error && <div className={styles.notice} role="alert"><AlertCircle size={18} />{error}<button type="button" onClick={() => void load()}>Retry</button></div>}
      {loading && !cycles.length && <ScreenSkeleton label="Loading configuration" variant="configuration" />}
      {!loading && !error && <>
        <AdminConfigPanel cycles={cycles} cycleNumber={cycleNumber} onCycleChanged={() => void load()} onCycleSelected={setCycleNumber} />
      </>}
    </>}
  </main></PageShell>;
}
